import axiosClient from "@/shared/lib/axios/axios.ts";
import { MachineEvent, TruckEvent, TruckEventOverview } from "@/machines/types/event.ts";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@/shared/types/api.ts";
import { API_ROUTES } from "@/machines/api/config.ts";
import { formatDateTime } from "@/shared/utils/date/format-datetime.ts";
import { getPaginationParams } from "@/shared/utils/pagination/pagination.ts";
import type { Change } from "@/machines/stores/useTimelineEditQueueStore.ts";
import { preciseDateCache } from "@/shared/utils/date/convert-datetime.tsx";


export interface IGetEventsInput {
  device_mxid?: string;
  start_time: Date;
  end_time: Date;
  page: number;
  itemsPerPage: number;
}

const getEvents = (params: IGetEventsInput, signal?: AbortSignal) => {

  const { offset, limit } = getPaginationParams(params.page, params.itemsPerPage)

  return axiosClient.get<IGetEventsInput, AxiosResponse<ApiResponse<MachineEvent[]>>>(API_ROUTES.EVENTS, {
    params: {
      device_mxid: params?.device_mxid,
      start_time: formatDateTime(params?.start_time, "start"),
      end_time: formatDateTime(params?.end_time, "end"),
      offset,
      limit
    },
    signal, // Pass the abort signal to axios
    preciseDate: true // Cache precise datetime values from API
  }).then(data => data.data);
}


export const getEventById = (id: string, signal?: AbortSignal) => {
  return axiosClient.get<IGetEventsInput, AxiosResponse<ApiResponse<MachineEvent>>>(`${API_ROUTES.EVENTS}${id}/`, {
    signal // Pass the abort signal to axios
  }).then(data => data.data);
}


export const getTruckEvents = (params: IGetEventsInput, signal?: AbortSignal) => {


  const { offset, limit } = getPaginationParams(params.page, params.itemsPerPage)

  return axiosClient.get<IGetEventsInput, AxiosResponse<ApiResponse<TruckEvent[]>>>(API_ROUTES.TRUCK_EVENTS, {
    params: {
      device_mxid: params?.device_mxid,
      start_time: formatDateTime(params?.start_time, "start"),
      end_time: formatDateTime(params?.end_time, "end"),
      offset,
      limit
    },
    signal,
  }).then(data => data.data);
}

export interface IGetTruckEventsOverviewInput {
  device_mxid?: string;
  start_time: Date;
  end_time: Date;
  page: number;
  itemsPerPage: number;
}


export const getTruckEventsOverview = (params: IGetTruckEventsOverviewInput, signal?: AbortSignal) => {


  const { offset, limit } = getPaginationParams(params.page, params.itemsPerPage)

  return axiosClient.get<IGetEventsInput, AxiosResponse<ApiResponse<TruckEventOverview>>>(API_ROUTES.TRUCK_EVENTS_OVERVIEW, {
    params: {
      device_mxid: params?.device_mxid,
      start_time: formatDateTime(params?.start_time, "start"),
      end_time: formatDateTime(params?.end_time, "end"),
      offset,
      limit
    },
    signal // Pass the abort signal to axios
  }).then(data => data.data);
}

// Batch edit types
export interface TruckEventBatchEditPayload {
  device_mxid: string;
  commands: Change[];
}

export interface TruckEventBatchEditResult {
  updated: TruckEvent[];
  created: TruckEvent[];
  deleted: string[]; // Array of deleted IDs
}

export interface TruckEventBatchEditError {
  command_index: number;
  error: string;
  details?: string;
}

export interface CacheDebugEntry {
  commandIndex: number;
  commandType: string;
  id: string;
  start_time: { original: string; resolved: string; source: 'cache' | 'fallback'; eventLayer?: string };
  end_time: { original: string; resolved: string; source: 'cache' | 'fallback'; eventLayer?: string };
}

export const batchEditTruckEvents = (payload: TruckEventBatchEditPayload) => {
  // Ensure all timestamps are properly formatted as ISO strings with microsecond precision
  // Also collect cache debug info for each command

  const cacheDebugInfo: CacheDebugEntry[] = [];

  const formattedPayload = {
    ...payload,
    commands: payload.commands.map((command, index) => {
      if (command.type === 'delete') {
        return command;
      }

      const formatToISOWithMicroseconds = (timestamp: string | number | Date, field: 'start' | 'end'): { resolved: string; source: 'cache' | 'fallback'; eventLayer?: string; original: string } => {
        // Convert everything to timestamp first for cache lookup
        let timestampMs: number;

        if (typeof timestamp === 'string') {
          timestampMs = new Date(timestamp).getTime();
        } else if (timestamp instanceof Date) {
          timestampMs = timestamp.getTime();
        } else {
          timestampMs = timestamp;
        }

        const original = String(timestamp);

        // Try to get the precise ISO string from cache first
        const cachedValue = preciseDateCache.get(timestampMs);
        if (cachedValue) {
          const isoString = field === 'start' ? cachedValue.start : cachedValue.end;
          return { resolved: isoString, source: 'cache', eventLayer: cachedValue.eventLayer, original };
        }

        // Fallback: Convert to ISO and add microseconds padding
        let isoString = new Date(timestampMs).toISOString();
        if (/\.\d{3}Z$/.test(isoString)) {
          isoString = isoString.replace(/(\.\d{3})Z$/, '$1000Z');
        }

        return { resolved: isoString, source: 'fallback', original };
      };

      const startResult = formatToISOWithMicroseconds(command.start_time, 'start');
      const endResult = formatToISOWithMicroseconds(command.end_time, 'end');

      cacheDebugInfo.push({
        commandIndex: index,
        commandType: command.type,
        id: command.id,
        start_time: startResult,
        end_time: endResult,
      });

      return {
        ...command,
        start_time: startResult.resolved,
        end_time: endResult.resolved,
      };
    })
  };

  return axiosClient.post<TruckEventBatchEditPayload, AxiosResponse<ApiResponse<TruckEventBatchEditResult>>>(
    API_ROUTES.TRUCK_EVENTS_BATCH_EDIT,
    formattedPayload
  ).then(data => data.data)
    .catch(error => {
      // Attach cache debug info to the error for debugging in the error modal
      error._cacheDebugInfo = cacheDebugInfo;
      throw error;
    });
}


export default getEvents;